<template>
  <Modal
    v-model="modelValue"
    :closeOnBackdropClick="false"
    class="modal-dialog-scrollable modal-style"
    style="max-width: 462px">
    <template #content>
      <div class="modal-header">
        <div class="d-flex flex-row align-items-center">
          <div class="d-flex flex-column">
            <div class="modal-title">{{ $t('orders.confirmation.title') }}</div>
            <div class="modal-subtitle">{{ props.jobReferenceId }}</div>
          </div>
        </div>
        <IconX size="24" @click="modelValue = false" class="btn-x" />
      </div>
      <div class="modal-body">
        <ProviderCard
          v-for="provider in providersWithUpdatedStatus"
          :key="provider.provider"
          :title="provider.provider_name"
          :image-src="hostname + provider.provider_logo"
          :provider="provider" />
      </div>
      <div class="modal-footer">
        <div class="footer-content">
          <span v-if="disablePublish" class="info-text">
            {{ publishBlockedMessage }}
          </span>
          <LeadButton
            type="button"
            variant="primary"
            :disabled="disablePublish"
            :loading="loadingPublish"
            @click="publish">
            <span style="font-size: 13px">
              {{ $t('orders.create.form.publish') }}
            </span>
            <template #icon>
              <IconCheck size="16"></IconCheck>
            </template>
          </LeadButton>
        </div>
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { Modal } from '@prospective/lithium';
import ProviderCard from './provider_cards/ProviderCard.vue';
import LeadButton from '../generic/LeadButton.vue';
import { IconX, IconCheck } from '@tabler/icons-vue';
import { useProviderStore } from '@/stores/providers';
import { storeToRefs } from 'pinia';
import { onMounted, computed } from 'vue';
import { useRoute } from 'vue-router';
import { publishCampaigns } from '@/api/campaign';
import { hostname } from '@/api/client';
import type { CampaignProvider } from '@/types/types';
import translator from '@/locale/translator';
import { formatDate } from '@/filters/dateFormat';

const modelValue = defineModel<boolean>({ default: false });
const providerStore = useProviderStore();
const route = useRoute();

const { providers, providersWithUpdatedStatus, loadingPublish } = storeToRefs(providerStore);

const props = defineProps<{
  orderId: string;
  jobReferenceId: string | null | undefined;
  confirmedByCustomer: boolean | null | undefined;
  startDate?: string | null;
}>();

onMounted(async () => {
  await providerStore.getProviders(route.params.id as string);
});

const disablePublish = computed(() => {
  const prov = providersWithUpdatedStatus.value;
  if (!prov || prov.length === 0) return false;

  return prov.some((provider) => {
    if (provider.finished) return false;
    return !provider.can_be_pushed || !provider.confirmed;
  });
});

// the order start date does not fall on today, which is what LinkedIn requires to publish
const startDateIsNotToday = computed(() => {
  if (!props.startDate) return false;
  return formatDate(props.startDate, 'yyyy-MM-dd') !== formatDate(new Date(), 'yyyy-MM-dd');
});

// a non-finished campaign whose only obstacle is LinkedIn's start-date rule: everything is filled in
// and confirmed, but LinkedIn only allows publishing on the campaign start date
// (see LinkedInProvider::canPushCampaign)
const isLinkedinStartDateBlock = (provider: CampaignProvider): boolean =>
  startDateIsNotToday.value &&
  Boolean(provider.provider?.toLowerCase().includes('linkedin')) &&
  provider.confirmed &&
  provider.values_valid &&
  !provider.can_be_pushed;

const blockingProviders = computed(() =>
  (providersWithUpdatedStatus.value ?? []).filter(
    (provider) => !provider.finished && (!provider.can_be_pushed || !provider.confirmed)
  )
);

const publishBlockedMessage = computed(() => {
  if (!props.confirmedByCustomer) return translator.get('orders.create.form.publish_tooltip_unconfirmed');

  // only surface the LinkedIn start-date hint when every blocking campaign is blocked solely by that rule,
  // otherwise something else (missing fields, unconfirmed campaign) is the real reason
  const blockers = blockingProviders.value;
  if (blockers.length > 0 && blockers.every(isLinkedinStartDateBlock)) {
    return translator.get('orders.create.form.publish_tooltip_linkedin', {
      date: formatDate(props.startDate),
    });
  }

  return translator.get('orders.create.form.publish_tooltip');
});

const publish = async () => {
  loadingPublish.value = true;

  try {
    const canBePushed = providers.value?.every((provider) => provider.can_be_pushed);
    if (canBePushed) {
      await publishCampaigns(props.orderId);
      providerStore.startPollingCampaigns(props.orderId);
    }
  } catch (error) {
    console.error('Error in confirm and publishing:', error);
  }
};
</script>

<style scoped lang="scss">
:deep(.modal-content) {
  box-shadow: 0px 0px 23.4px 0px #0000003d;
  border-left: 1px solid #eaecf0;
}

.modal-header {
  padding: 13px 20px;
  height: 74px;
  display: flex;
  align-items: center;

  .info-help-icon {
    margin-right: 16px;
  }

  .modal-title {
    font-size: 16px;
    line-height: 24px;
    font-weight: 600;
    color: #101828;
  }

  .modal-subtitle {
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    color: #475467;
  }

  .btn-x {
    position: relative;
    color: #8e939d;
    cursor: pointer;
  }
}

.modal-body {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  padding: 20px;
  border-bottom: 1px solid #eaecf0;
  max-width: 100%;
  max-height: 600px;
  overflow-y: auto;
  overflow-x: hidden;

  > * {
    flex: 0 0 calc(50% - 10px);
    min-width: 0;
    max-width: calc(50% - 10px);
  }
}

.modal-footer {
  background: #f8f8f8;
  padding: 12px 20px;
  .footer-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;

    .info-text {
      font-size: 12px;
      color: #6b7280;
      flex: 1;
      max-width: 280px;
      line-height: 1.4;
    }
  }
}
</style>
